import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { locations } from '../mockData';
import { FootballSubMark, RacquetSubMark } from '../components/SportBlocLogo';

const Locations = () => {
  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-600 to-yellow-400 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-black mb-4">Our Locations</h1>
          <p className="text-xl text-gray-900">Premium sports facilities across Shillong</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {locations.map((location, index) => (
          <div key={location.id} className={`mb-16 ${index > 0 ? 'pt-16 border-t border-yellow-500/20' : ''}`}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              {/* Image */}
              <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="relative h-96 rounded-2xl overflow-hidden group">
                  <img
                    src={location.heroImage}
                    alt={location.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    {location.id === 1
                      ? <FootballSubMark size={80} />
                      : <RacquetSubMark size={80} />}
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className="inline-block px-4 py-1 bg-yellow-400/20 border border-yellow-400 rounded-full mb-4">
                  <span className="text-yellow-400 font-semibold text-sm">{location.sport}</span>
                </div>
                
                <h2 className="text-4xl font-bold text-white mb-4">{location.name}</h2>
                <p className="text-gray-400 text-lg mb-6">{location.description}</p>

                {/* Info */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center text-gray-300">
                    <MapPin className="w-5 h-5 text-yellow-400 mr-3" />
                    <span>{location.address}</span>
                  </div>
                  <div className="flex items-center text-gray-300">
                    <Clock className="w-5 h-5 text-yellow-400 mr-3" />
                    <span>{location.timings}</span>
                  </div>
                  <div className="flex items-center text-gray-300">
                    <Phone className="w-5 h-5 text-yellow-400 mr-3" />
                    <span>{location.contact}</span>
                  </div>
                </div>

                {/* Features */}
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-white mb-3">Features</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {location.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-yellow-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-400 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pricing */}
                <div className="bg-gray-900 border border-yellow-500/20 rounded-xl p-6 mb-6">
                  <h3 className="text-xl font-semibold text-white mb-4">Pricing</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-gray-400 text-sm mb-1">Weekday</div>
                      <div className="text-2xl font-bold text-yellow-400">₹{location.pricing.weekday}</div>
                      <div className="text-gray-500 text-xs">per hour</div>
                    </div>
                    <div>
                      <div className="text-gray-400 text-sm mb-1">Weekend</div>
                      <div className="text-2xl font-bold text-yellow-400">₹{location.pricing.weekend}</div>
                      <div className="text-gray-500 text-xs">per hour</div>
                    </div>
                  </div>
                </div>

                <Link to="/book">
                  <Button size="lg" className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold w-full sm:w-auto">
                    Book This Location <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Amenities */}
            <div className="mt-12">
              <h3 className="text-2xl font-bold text-white mb-6 text-center">Amenities</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {location.amenities.map((amenity, idx) => (
                  <Card key={idx} className="bg-gray-900 border-yellow-500/20 hover:border-yellow-400 transition-all">
                    <CardContent className="p-4 text-center">
                      <div className="text-3xl mb-2">{amenity.icon}</div>
                      <h4 className="text-sm font-semibold text-yellow-400 mb-1">{amenity.name}</h4>
                      <p className="text-xs text-gray-500">{amenity.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Locations;